package org.example.project.registrierung

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object DesktopRegistrierungApi {
    private const val API = "desktopregistrierungscreen"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    data class LoginResult(val user: All? = null, val error: String = "")

    suspend fun login(email: String, password: String): All? = loginDetailed(email, password).user

    suspend fun loginDetailed(email: String, password: String): LoginResult = runCatching {
        if (org.example.project.service.reloadForLatestLoginBuild()) return@runCatching LoginResult(error = "build_reload")
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/login-json",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(buildJsonObject {
                    put("email", email.trim().lowercase())
                    put("password", password)
                    put("mode", "desktop")
                })
            )
        ).await()
        val raw = response.text().await()
        if (response.ok) LoginResult(decodeOne(raw)) else {
            val code = runCatching { json.parseToJsonElement(raw).jsonObject["error"]?.jsonPrimitive?.contentOrNull }
                .getOrNull().orEmpty()
            LoginResult(error = code.ifBlank { if (response.status.toInt() == 429) "rate_limited" else "server_error" })
        }
    }.getOrElse { LoginResult(error = "network_error") }

    suspend fun checkZweiFaktor(token: String, code: String, trustedDevice: Boolean): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/zwei-faktor-pruefen",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        put("token", token)
                        put("code", code)
                        put("trustedDevice", trustedDevice)
                    }
                )
            )
        ).await()
        if (!response.ok) return null
        return decodeOne(response.text().await())
    }

    suspend fun verifyDesktopAccess(email: String, password: String, snapKey: String): All? {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/verify-desktop-json",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        put("email", email.trim().lowercase())
                        put("password", password)
                        put("snapKey", snapKey.trim())
                    }
                )
            )
        ).await()
        if (!response.ok) return null
        return decodeOne(response.text().await())
    }

    suspend fun emailExists(email: String): Boolean {
        val raw = LumaticaApiManager.call("$API/email-exists/${apiPart(email.lowercase())}")
        return raw.contains("\"exists\":true")
    }

    suspend fun invitationOwnerFirma(einladungsKey: String): String? = runCatching {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/invitation-check",
            RequestInit(method = "POST", headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(buildJsonObject { put("einladungsKey", einladungsKey.trim()) }))
        ).await()
        val result = json.parseToJsonElement(response.text().await()).jsonObject
        if (response.ok && result["ok"]?.jsonPrimitive?.contentOrNull == "true") {
            result["firmenname"]?.jsonPrimitive?.contentOrNull?.takeIf { it.isNotBlank() }
        } else null
    }.getOrNull()

    suspend fun changePassword(userId: String, firmenId: String, password: String): All? =
        decodeOne(postPasswordJson("$API/password-change-json", userId, firmenId, password))

    suspend fun registeredCompanies(): List<All> =
        runCatching {
            json.decodeFromString(
                ListSerializer(All.AllNames.serializer()),
                LumaticaApiManager.call("registrierung/companies")
            )
        }.getOrDefault(emptyList())

    suspend fun registerDesktopCompany(firma: All, user: All, password: String, signatur: String, einladungsKey: String): All {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$API/register-json",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        put("password", password)
                        put("signatur", signatur)
                        put("einladungsKey", einladungsKey.trim())
                        put("firma", json.encodeToJsonElement(All.AllNames.serializer(), firma))
                        put("user", json.encodeToJsonElement(All.AllNames.serializer(), user))
                    }
                )
            )
        ).await()
        val raw = response.text().await()
        val result = runCatching { json.parseToJsonElement(raw).jsonObject }.getOrNull()
        val error = result?.get("error")?.jsonPrimitive?.contentOrNull.orEmpty()
        if (!response.ok || error.isNotBlank()) {
            val message = when (error) {
                "email_invalid" -> "Bitte eine gültige E-Mail-Adresse eingeben."
                "email_exists" -> "E-Mail existiert bereits."
                "invitation_invalid" -> "Der Einladungskey ist ungültig, abgelaufen oder bereits verwendet."
                else -> "Registrierung konnte nicht gespeichert werden."
            }
            throw IllegalStateException(message)
        }
        return decodeOne(raw) ?: throw IllegalStateException("Registrierung konnte nicht gespeichert werden.")
    }

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private suspend fun postPasswordJson(endpoint: String, first: String, firmenId: String, password: String): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        if (endpoint.endsWith("login-json")) {
                            put("email", first.trim().lowercase())
                            put("mode", "desktop")
                        } else {
                            put("userId", first)
                            put("firmenId", firmenId)
                            put("mode", "desktop")
                        }
                        put("password", password)
                    }
                )
            )
        ).await()
        return if (response.ok) response.text().await() else ""
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}
